import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { DASHBOARD_STATES } from '../constants';
import { readPublishRejection } from '../utils/publishRejection.js';
import { useDashboardData } from '../context';

const INITIAL_EDITOR_STATE = {
  id: null,
  titleKey: '',
  descriptionKey: '',
  title: '',
  hasDuplicateTitleSuffix: false,
  description: '',
  state: DASHBOARD_STATES.DRAFT,
  sectionId: null,
  sectionPath: '',
  widgets: [],
  sharedWith: [],
  loading: false,
  saving: false,
  error: null,
  isDirty: false,
};

const buildPayload = (source, state = null) => ({
  title: String(source?.title || '').trim() || 'Untitled Dashboard',
  description: source?.description || '',
  state: state ?? undefined,
  sectionId: source?.sectionId || null,
  sectionPath: source?.sectionPath || '',
  widgets: Array.isArray(source?.widgets) ? source.widgets : [],
  sharedWith: Array.isArray(source?.sharedWith) ? source.sharedWith : [],
  hasDuplicateTitleSuffix: Boolean(source?.hasDuplicateTitleSuffix),
});

export const useDashboardEditor = () => {
  const { t } = useTranslation('dashboards');
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const { createDashboard, getDashboardById, publishDashboard, updateDashboard } = useDashboardData();

  const loadRequestIdRef = useRef(0);
  const savingRef = useRef(false);
  const editorStateRef = useRef(INITIAL_EDITOR_STATE);
  /**
   * Instance ids the *server* holds, which is not the same as the ones on
   * screen. `update` never deletes a placement it was not sent, so this is what
   * says whether a save actually removes what the author removed.
   */
  const storedInstanceIdsRef = useRef(new Set());
  const [editorState, setEditorState] = useState(INITIAL_EDITOR_STATE);

  const isEditing = Boolean(dashboardId && dashboardId !== 'new');
  editorStateRef.current = editorState;

  const updateEditor = useCallback((updates) => {
    setEditorState((prev) => ({
      ...prev,
      ...updates,
      isDirty: true,
    }));
  }, []);

  useEffect(() => {
    if (!isEditing) {
      loadRequestIdRef.current += 1;
      storedInstanceIdsRef.current = new Set();
      setEditorState(INITIAL_EDITOR_STATE);
      return;
    }

    const requestId = ++loadRequestIdRef.current;
    let isActive = true;
    const loadDashboard = async () => {
      setEditorState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const body = await getDashboardById(dashboardId);
        if (!isActive || requestId !== loadRequestIdRef.current) return;

        storedInstanceIdsRef.current = new Set(
          (Array.isArray(body.widgets) ? body.widgets : [])
            .map((widget) => widget.instanceId)
            .filter(Boolean),
        );

        setEditorState({
          id: body.id || dashboardId,
          titleKey: body.titleKey || '',
          descriptionKey: body.descriptionKey || '',
          title: body.title || '',
          hasDuplicateTitleSuffix: Boolean(body.hasDuplicateTitleSuffix),
          description: body.description || '',
          state: body.state || DASHBOARD_STATES.DRAFT,
          sectionId: body.sectionId || null,
          sectionPath: body.sectionPath || '',
          widgets: Array.isArray(body.widgets) ? body.widgets : [],
          sharedWith: Array.isArray(body.sharedWith) ? body.sharedWith : [],
          loading: false,
          saving: false,
          error: null,
          isDirty: false,
        });
      } catch (error) {
        if (!isActive || requestId !== loadRequestIdRef.current) return;
        setEditorState((prev) => ({ ...prev, loading: false, error }));
      }
    };

    loadDashboard();
    return () => {
      isActive = false;
      loadRequestIdRef.current += 1;
    };
  }, [dashboardId, getDashboardById, isEditing]);

  /**
   * Which stored placements this save leaves behind.
   *
   * `update`'s composition is upsert-only: what it is sent is created or
   * updated, and **children left out are kept**. There is no child-removal path
   * this phase, so a widget deleted from the canvas is still on the dashboard
   * after saving, and reappears on the next read. The editor cannot fix that,
   * but it can stop the user finding out by reloading.
   */
  const widgetsThatWillSurvive = useCallback(() => {
    const stored = storedInstanceIdsRef.current;
    if (!stored.size) return [];
    const kept = new Set(editorStateRef.current.widgets.map((w) => w.instanceId));
    return [...stored].filter((id) => !kept.has(id));
  }, []);

  const saveDraft = useCallback(async () => {
    if (savingRef.current) return { success: false };
    savingRef.current = true;
    setEditorState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const current = editorStateRef.current;
      const payload = buildPayload(current, DASHBOARD_STATES.DRAFT);

      // Read before the write: the ids the server is about to keep.
      const survivors = widgetsThatWillSurvive();
      const rememberStored = () => {
        // What the server holds afterwards: everything sent, plus everything it
        // declined to remove.
        storedInstanceIdsRef.current = new Set([
          ...editorStateRef.current.widgets.map((w) => w.instanceId).filter(Boolean),
          ...survivors,
        ]);
      };

      // The id, not the route. `create` is two calls — metadata, then the
      // composition — so a half-finished one leaves a real dashboard while the
      // URL is still `/new`. Branching on `isEditing` would then create a
      // *second* dashboard on the retry; branching on the id the editor holds
      // turns that retry into the `update` that finishes the job.
      const existingId = current.id || (isEditing ? dashboardId : null);

      if (!existingId) {
        const created = await createDashboard(payload);
        const createdId = created?.id;
        setEditorState((prev) => ({
          ...prev,
          id: createdId || prev.id,
          state: DASHBOARD_STATES.DRAFT,
          saving: false,
          isDirty: false,
        }));
        if (createdId) {
          rememberStored();
          navigate(`/dashboards/${createdId}/edit`, { replace: true });
          return { success: true, notRemoved: survivors };
        }
      } else {
        const idToSave = existingId;
        await updateDashboard(idToSave, payload);
        setEditorState((prev) => ({
          ...prev,
          state: DASHBOARD_STATES.DRAFT,
          saving: false,
          isDirty: false,
        }));
        if (idToSave) {
          rememberStored();
          return { success: true, notRemoved: survivors };
        }
      }

      rememberStored();
      return { success: true, notRemoved: survivors };
    } catch (error) {
      // The dashboard may already exist: when only the composition write
      // failed, the adapter hands its id back on the error. Adopting it is what
      // makes the retry an `update` instead of a second dashboard.
      //
      // **Deliberately no navigation.** Moving to `/:id/edit` would remount the
      // editor and reload the server's draft — which has no placements yet,
      // because writing them is exactly what failed. That wipes the canvas and
      // leaves the retry with nothing to resend.
      const createdId = error?.dashboardId;
      setEditorState((prev) => ({
        ...prev, saving: false, error, id: createdId || prev.id,
      }));
      return { success: false, error };
    } finally {
      savingRef.current = false;
    }
  }, [createDashboard, dashboardId, isEditing, navigate, updateDashboard, widgetsThatWillSurvive]);

  const publish = useCallback(
    async (overrides = {}) => {
      if (savingRef.current) return { success: false };
      savingRef.current = true;
      const current = { ...editorStateRef.current, ...overrides };
      const validationErrors = [];

      try {
        if (!String(current.title || '').trim()) validationErrors.push('title');
        if (!current.sectionId) validationErrors.push('sectionId');
        if (validationErrors.length) return { success: false, validationErrors };

        /*
          Publishing no longer waits for a share (CT-5552).

          It used to return `needsShare` whenever `sharedWith` was empty, and
          the page answered by opening the Share modal — whose own Share
          button is disabled with no recipients, so the only route to
          publishing ran through a directory that is still mock data
          (CT-5893). Sharing has its own button in the header; these are two
          actions, not one wizard.
        */

        setEditorState((prev) => ({ ...prev, saving: true, error: null }));
        const payload = buildPayload(current);

        let idToPublish = current.id || dashboardId;
        if (!idToPublish) {
          const created = await createDashboard({ ...payload, state: DASHBOARD_STATES.DRAFT });
          idToPublish = created?.id || null;
        } else {
          await updateDashboard(idToPublish, payload);
        }

        if (!idToPublish) throw new Error('Unable to resolve dashboard id for publish');
        await publishDashboard(idToPublish, { versionNotes: current.versionNotes });

        setEditorState((prev) => ({
          ...prev,
          id: idToPublish,
          state: DASHBOARD_STATES.PUBLISHED,
          saving: false,
          isDirty: false,
        }));

        navigate(`/dashboards/${idToPublish}`, {
          state: {
            toast: {
              severity: 'success',
              title: t('editor.toasts.published'),
              description: t('editor.toasts.publishedDescription'),
            },
          },
        });
        return { success: true };
      } catch (error) {
        // Same as `saveDraft`: a half-finished create leaves a real draft, and
        // `current.id` is what keeps the next attempt from making another one.
        const createdId = error?.dashboardId;
        setEditorState((prev) => ({
          ...prev, saving: false, error, id: createdId || prev.id,
        }));
        /*
          A rejected publish is `DSW022`, and the reason the author needs is
          in the report the backend attached, not in the generic message:
          "Section home-overview is not published" tells them what to change,
          "couldn't publish" does not. `validateApiResponse` leaves the whole
          envelope on `error.response.data`, and the report arrives under
          either key depending on whether it passed or was rejected.
        */
        return { success: false, error, ...readPublishRejection(error) };
      } finally {
        savingRef.current = false;
      }
    },
    [createDashboard, dashboardId, navigate, publishDashboard, t, updateDashboard],
  );

  const addWidgets = useCallback((newWidgets) => {
    if (!Array.isArray(newWidgets) || !newWidgets.length) return;

    setEditorState((prev) => {
      const nextWidgets = newWidgets.map((widget, index) => {
        const instanceId = widget.instanceId || `${Date.now()}_${widget.type || 'widget'}_${prev.widgets.length + index}`;
        return {
          ...widget,
          instanceId,
          layout: null,
          actions: widget.actions || widget.defaultActions || [],
          locked: widget.locked ?? false,
        };
      });

      return {
        ...prev,
        widgets: [...prev.widgets, ...nextWidgets],
        isDirty: true,
      };
    });
  }, []);

  const removeWidget = useCallback((instanceId) => {
    setEditorState((prev) => ({
      ...prev,
      widgets: prev.widgets.filter((item) => item.instanceId !== instanceId),
      isDirty: true,
    }));
  }, []);

  const toggleWidgetLock = useCallback((instanceId) => {
    setEditorState((prev) => ({
      ...prev,
      widgets: prev.widgets.map((item) =>
        item.instanceId === instanceId ? { ...item, locked: !item.locked } : item,
      ),
      isDirty: true,
    }));
  }, []);

  const updateLayouts = useCallback((_layout, allLayouts) => {
    if (!allLayouts) return;
    const lgLayout = allLayouts.lg || [];
    setEditorState((prev) => {
      const nextWidgets = prev.widgets.map((item) => {
        const match = lgLayout.find((layoutItem) => layoutItem.i === item.instanceId);
        if (!match) return item;
        return {
          ...item,
          layout: { i: match.i, x: match.x, y: match.y, w: match.w, h: match.h },
        };
      });
      return { ...prev, widgets: nextWidgets, isDirty: true };
    });
  }, []);

  const canAddWidgets = Boolean(editorState.sectionId);

  return {
    ...editorState,
    isEditing,
    canAddWidgets,
    updateEditor,
    saveDraft,
    publish,
    addWidgets,
    removeWidget,
    toggleWidgetLock,
    updateLayouts,
  };
};

export default useDashboardEditor;
